import {
  EVENT_LISTENER_METHODS,
  eventBus,
  MEDIA_TYPE,
  toggleVideoTrack,
  updateVideoInputDeviceState,
  useBaseStore,
  useHideToggleVideoButton,
  useIsVideoInputSelectionSupported,
  useTrackMuted,
  useTracksStore,
  useTranslation,
  type VideoInputDevice,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../icon/Icon.web';
import Settings from '../Settings.web';

const VideoControl: React.FC = () => {
  const videoMuted = useTrackMuted(MEDIA_TYPE.VIDEO);
  const { t } = useTranslation();

  const hideToggleVideoButton = useHideToggleVideoButton();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const videoInputDevices = useBaseStore(state => state.videoInputDevices);
  const hasVideoPermission = useTracksStore(state => state.permissions.video);

  const isVideoInputSelectionSupported = useIsVideoInputSelectionSupported();

  const videoInputDevice = useBaseStore(state => state.videoInputDevice);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (isDropdownOpen || isMenuOpen)) {
        setIsDropdownOpen(false);
        setIsMenuOpen(false);
      }
    };

    if (isDropdownOpen || isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDropdownOpen, isMenuOpen]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
    setIsMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleCameraDeviceSelect = useCallback(
    async (device: MediaDeviceInfo) => {
      updateVideoInputDeviceState(device as VideoInputDevice);
      setIsMenuOpen(false);
    },
    [],
  );

  const onVideoToggleClick = useCallback(() => {
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.ButtonClickListener
        .onToggleVideoButtonClicked,
    });
    toggleVideoTrack();
  }, []);

  if (hideToggleVideoButton) {
    return null;
  }

  const videoDeviceSelector = () => {
    if (!videoInputDevice) {
      return null;
    }
    return (
      <div className="cometchat-calls-video-selector">
        <button
          className="cometchat-calls-video-selector__button"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-haspopup="listbox">
          <div className="cometchat-calls-video-selector__content">
            <Icon
              name="video-fill"
              fill="var(--cometchat-calls-static-white)"
              size={24}
            />
            <span className="cometchat-calls-video-selector__device-label">
              {videoInputDevice.label}
            </span>
            <div
              className={clsx('cometchat-calls-video-selector__arrow', {
                'cometchat-calls-video-selector__arrow--open': isMenuOpen,
              })}>
              <Icon
                name="arrow-up"
                fill="var(--cometchat-calls-static-white)"
                size={24}
              />
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="cometchat-calls-video-control" ref={dropdownRef}>
      {!hasVideoPermission && (
        <div className="cometchat-calls-video-control__warning">
          {' '}
          <Icon
            name="alert"
            fill="var(--cometchat-calls-warning-color)"
            size={20}
          />
        </div>
      )}

      <Settings
        selectedTab="video"
        visible={isSettingsOpen}
        onClose={toggleSettings}
      />
      {isDropdownOpen && (
        <div className="cometchat-calls-video-control__dropdown-wrapper">
          {isMenuOpen && (
            <div
              className="cometchat-calls-video-control__device-menu"
              role="listbox">
              {videoInputDevices.map(device => (
                <div
                  className={clsx(
                    'cometchat-calls-video-control__device-menu-item',
                    {
                      'cometchat-calls-video-control__device-menu-item--selected':
                        device.deviceId === videoInputDevice?.deviceId,
                    },
                  )}
                  onClick={() => handleCameraDeviceSelect(device)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCameraDeviceSelect(device);
                    }
                  }}
                  key={device.deviceId}
                  role="option"
                  aria-selected={device.deviceId === videoInputDevice?.deviceId}
                  tabIndex={0}>
                  <div className="cometchat-calls-video-control__device-menu-icon">
                    {device.deviceId === videoInputDevice?.deviceId ? (
                      <Icon
                        name="check-fill"
                        fill="var(--cometchat-calls-static-white)"
                        size={24}
                      />
                    ) : null}
                  </div>
                  <span className="cometchat-calls-video-control__device-menu-label">
                    {device.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="cometchat-calls-video-control__dropdown-bar">
            <div className="cometchat-calls-video-control__section">
              {videoDeviceSelector()}
            </div>

            <div className="cometchat-calls-video-control__section cometchat-calls-video-control__section--settings">
              <button
                className="cometchat-calls-video-control__settings-button"
                onClick={toggleSettings}
                aria-label={t('controlPanel.videoControl.videoSettings')}
                title={t('controlPanel.videoControl.videoSettings')}>
                <Icon
                  name="settings"
                  fill="var(--cometchat-calls-static-white)"
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={clsx('cometchat-calls-video-control__wrapper', {
          'cometchat-calls-video-control__wrapper--active': videoMuted,
        })}>
        <div
          className="cometchat-calls-video-control__button-wrapper"
          onClick={onVideoToggleClick}
          data-testid="cometchat-video-toggle-button"
          aria-label={
            videoMuted
              ? t('controlPanel.videoControl.turnOnCamera')
              : t('controlPanel.videoControl.turnOffCamera')
          }>
          <button
            className="cometchat-calls-video-control__button"
            title={
              videoMuted
                ? t('controlPanel.videoControl.turnOnCamera')
                : t('controlPanel.videoControl.turnOffCamera')
            }>
            <Icon
              name={videoMuted ? 'video-off-fill' : 'video-fill'}
              fill={
                videoMuted
                  ? 'var(--cometchat-calls-error-color)'
                  : 'var(--cometchat-calls-static-white)'
              }
            />
          </button>
        </div>
        <div
          className="cometchat-calls-video-control__divider"
          onClick={onVideoToggleClick}
        />
        <div
          className="cometchat-calls-video-control__arrow-wrapper"
          onClick={isVideoInputSelectionSupported ? toggleDropdown : undefined}
          aria-disabled={!isVideoInputSelectionSupported}>
          <button
            disabled={!isVideoInputSelectionSupported}
            className={clsx('cometchat-calls-video-control__arrow-button', {
              'cometchat-calls-video-control__arrow-button-open':
                isDropdownOpen,
            })}
            aria-label={t('controlPanel.videoControl.videoDeviceOptions')}
            aria-expanded={isDropdownOpen}
            title={t('controlPanel.videoControl.videoDeviceOptions')}>
            <Icon
              name="arrow-up"
              fill="var(--cometchat-calls-static-white)"
              size={16}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoControl;
